/**
 * Formula Evaluator for Calculated Columns
 * Supports Excel-like formulas with column references
 *
 * Supported functions:
 * - Basic math: +, -, *, /, %
 * - Functions: SUM, AVG, MIN, MAX, IF, ROUND, ABS, LOOKUP
 * - Comparisons: =, !=, <, >, <=, >=
 * - Logical: AND, OR, NOT
 *
 * Column references: Use column keys in square brackets, e.g., [nettWeight]
 * Examples:
 * - [nettWeight] * 1.1
 * - IF([ticketType] = "Dispatch", [nettWeight], 0)
 * - ROUND([nettWeight] / 1000, 2)
 * - LOOKUP([deliveryNo], "ticketNo", "vehicleReg")
 */

class FormulaEvaluator {
  constructor() {
    this.allRowsData = []; // Store all rows for LOOKUP function
    this._lookupCache = {}; // Index cache for LOOKUP performance
    this.functions = {
      SUM: (...args) => args.reduce((sum, val) => sum + (parseFloat(val) || 0), 0),
      AVG: (...args) => {
        const nums = args.filter((v) => !isNaN(parseFloat(v)));
        return nums.length > 0
          ? nums.reduce((sum, val) => sum + parseFloat(val), 0) / nums.length
          : 0;
      },
      MIN: (...args) => Math.min(...args.map((v) => parseFloat(v) || 0)),
      MAX: (...args) => Math.max(...args.map((v) => parseFloat(v) || 0)),
      ROUND: (num, decimals = 0) => {
        const multiplier = Math.pow(10, decimals);
        return Math.round(parseFloat(num) * multiplier) / multiplier;
      },
      ABS: (num) => Math.abs(parseFloat(num) || 0),
      IF: (condition, trueValue, falseValue) => (condition ? trueValue : falseValue),
      IFS: (...args) => {
        // IFS takes pairs of condition-value arguments
        // IFS(condition1, value1, condition2, value2, ..., defaultValue)
        // Returns the value for the first true condition
        if (args.length === 0) return null;

        // Check if we have an odd number of args (includes default value)
        const hasDefault = args.length % 2 === 1;
        const defaultValue = hasDefault ? args[args.length - 1] : null;
        const pairCount = hasDefault ? (args.length - 1) / 2 : args.length / 2;

        // Evaluate condition-value pairs
        for (let i = 0; i < pairCount; i++) {
          const condition = args[i * 2];
          const value = args[i * 2 + 1];
          if (condition) {
            return value;
          }
        }

        // No condition matched, return default
        return defaultValue;
      },
      AND: (...args) => args.every((v) => v),
      OR: (...args) => args.some((v) => v),
      NOT: (val) => !val,
      UPPER: (str) => String(str).toUpperCase(),
      LOWER: (str) => String(str).toLowerCase(),
      LEN: (str) => String(str).length,
      CONCAT: (...args) => args.join(''),
      LEFT: (str, len) => String(str).substring(0, len),
      RIGHT: (str, len) => String(str).substring(String(str).length - len),
      TRIM: (str) => String(str).trim(),
      // LOOKUP function - searches for a value in another row
      // Uses indexed cache for O(1) lookups after initial O(n) index build
      LOOKUP: (searchValue, searchColumn, returnColumn, defaultValue = null) => {
        const isTransformation =
          typeof searchColumn === 'string' &&
          (searchColumn.includes('(') || searchColumn.includes('['));

        // Build or retrieve index for this searchColumn (cached per dataset)
        const cacheKey = searchColumn;
        if (!this._lookupCache[cacheKey]) {
          const index = new Map();
          let tempEvaluator;
          if (isTransformation) {
            tempEvaluator = new FormulaEvaluator();
            tempEvaluator.allRowsData = this.allRowsData;
          }

          this.allRowsData.forEach((row) => {
            let cellValue;
            if (isTransformation) {
              try {
                cellValue = tempEvaluator.evaluateSimple(searchColumn, row);
              } catch (error) {
                return; // skip this row
              }
            } else {
              cellValue = row[searchColumn];
            }
            const key = String(cellValue);
            if (key && !index.has(key)) {
              index.set(key, row); // first match wins (same as .find() behavior)
            }
          });
          this._lookupCache[cacheKey] = index;
        }

        // O(1) indexed lookup
        const foundRow = this._lookupCache[cacheKey].get(String(searchValue));

        if (foundRow) {
          if (
            typeof returnColumn === 'string' &&
            (returnColumn.includes('(') || returnColumn.includes('['))
          ) {
            try {
              return this.evaluateSimple(returnColumn, foundRow);
            } catch (error) {
              return defaultValue;
            }
          }
          return foundRow[returnColumn] !== undefined ? foundRow[returnColumn] : defaultValue;
        }
        return defaultValue;
      },
    };
  }

  /**
   * Set all rows data for LOOKUP function
   * @param {array} allRows - Array of all row data objects
   */
  setAllRowsData(allRows) {
    // Only clear LOOKUP cache if data actually changed (different array reference)
    if (this.allRowsData !== allRows) {
      this.allRowsData = allRows || [];
      this._lookupCache = {};
    }
  }

  /**
   * Simple evaluation without LOOKUP support (for use within LOOKUP transformations)
   * @param {string} formula - The formula to evaluate
   * @param {object} rowData - The row data with column values
   * @returns {any} The calculated result
   */
  evaluateSimple(formula, rowData) {
    try {
      // Replace column references [columnKey] with actual values
      let processedFormula = formula;

      // Find all column references
      const columnRefs = formula.match(/\[([^\]]+)\]/g);
      if (columnRefs) {
        columnRefs.forEach((ref) => {
          const columnKey = ref.slice(1, -1); // Remove brackets
          let value = rowData[columnKey];

          // Handle different data types
          if (value === null || value === undefined) {
            value = 0;
          } else if (typeof value === 'string') {
            // Escape quotes in strings
            value = `"${value.replace(/"/g, '\\"')}"`;
          } else if (value instanceof Date) {
            value = `"${value.toISOString()}"`;
          }

          processedFormula = processedFormula.replace(ref, value);
        });
      }

      // Replace function calls with JavaScript equivalents (excluding LOOKUP to avoid recursion)
      Object.keys(this.functions).forEach((funcName) => {
        if (funcName !== 'LOOKUP') {
          // Skip LOOKUP in simple evaluation
          const regex = new RegExp(`\\b${funcName}\\s*\\(`, 'gi');
          processedFormula = processedFormula.replace(regex, `this.functions.${funcName}(`);
        }
      });

      // Replace comparison operators
      processedFormula = processedFormula.replace(/\s*=\s*/g, ' === ');
      processedFormula = processedFormula.replace(/\s*!=\s*/g, ' !== ');

      // Evaluate the formula
      const result = eval(processedFormula);

      return result;
    } catch (error) {
      console.error('Simple formula evaluation error:', error, 'Formula:', formula);
      throw error;
    }
  }

  /**
   * Evaluate a formula for a given row of data
   * @param {string} formula - The formula to evaluate
   * @param {object} rowData - The row data with column values
   * @param {array} allRows - Optional: all rows for LOOKUP function
   * @returns {any} The calculated result
   */
  evaluate(formula, rowData, allRows = null) {
    // Update allRowsData if provided
    if (allRows) {
      this.setAllRowsData(allRows);
    }
    try {
      let processedFormula = formula;

      // Step 1: Protect quoted strings by temporarily replacing them with placeholders
      const quotedStrings = [];
      let stringIndex = 0;
      processedFormula = processedFormula.replace(/"([^"]*)"/g, (match) => {
        quotedStrings.push(match);
        return `__STRING_${stringIndex++}__`;
      });

      // Step 2: Replace column references [columnKey] with actual values (only outside quoted strings)
      const columnRefs = processedFormula.match(/\[([^\]]+)\]/g);
      if (columnRefs) {
        columnRefs.forEach((ref) => {
          const columnKey = ref.slice(1, -1); // Remove brackets
          let value = rowData[columnKey];

          // Handle different data types
          if (value === null || value === undefined) {
            value = 0;
          } else if (typeof value === 'string') {
            // Escape quotes in strings
            value = `"${value.replace(/"/g, '\\"')}"`;
          } else if (value instanceof Date) {
            value = `"${value.toISOString()}"`;
          }

          processedFormula = processedFormula.replace(ref, value);
        });
      }

      // Step 3: Restore quoted strings (these will contain untouched [columnKey] references for LOOKUP)
      quotedStrings.forEach((str, index) => {
        processedFormula = processedFormula.replace(`__STRING_${index}__`, str);
      });

      // Step 3.5: Re-protect quoted strings so Steps 4-5 don't modify LOOKUP transformation expressions
      const protectedStrings = [];
      let protectedIndex = 0;
      processedFormula = processedFormula.replace(/"([^"]*)"/g, (match) => {
        protectedStrings.push(match);
        return `__PROTECTED_${protectedIndex++}__`;
      });

      // Step 4: Replace function calls with JavaScript equivalents
      Object.keys(this.functions).forEach((funcName) => {
        const regex = new RegExp(`\\b${funcName}\\s*\\(`, 'gi');
        processedFormula = processedFormula.replace(regex, `this.functions.${funcName}(`);
      });

      // Step 5: Replace comparison operators
      processedFormula = processedFormula.replace(/\s*=\s*/g, ' === ');
      processedFormula = processedFormula.replace(/\s*!=\s*/g, ' !== ');

      // Step 5.5: Restore protected strings with original content intact
      protectedStrings.forEach((str, index) => {
        processedFormula = processedFormula.replace(`__PROTECTED_${index}__`, str);
      });

      // Step 6: Evaluate the formula
      // eslint-disable-next-line no-eval
      const result = eval(processedFormula);

      return result;
    } catch (error) {
      console.error('Formula evaluation error:', error, 'Formula:', formula);
      return '#ERROR';
    }
  }

  /**
   * Validate a formula syntax
   * @param {string} formula - The formula to validate
   * @param {array} availableColumns - Array of available column keys
   * @returns {object} { valid: boolean, error: string }
   */
  validate(formula, availableColumns) {
    try {
      // Check for balanced brackets
      const openBrackets = (formula.match(/\[/g) || []).length;
      const closeBrackets = (formula.match(/\]/g) || []).length;
      if (openBrackets !== closeBrackets) {
        return { valid: false, error: 'Unbalanced square brackets' };
      }

      // Check for balanced parentheses
      const openParens = (formula.match(/\(/g) || []).length;
      const closeParens = (formula.match(/\)/g) || []).length;
      if (openParens !== closeParens) {
        return { valid: false, error: 'Unbalanced parentheses' };
      }

      // Check if all column references exist
      const columnRefs = formula.match(/\[([^\]]+)\]/g);
      if (columnRefs) {
        for (const ref of columnRefs) {
          const columnKey = ref.slice(1, -1);
          if (!availableColumns.includes(columnKey)) {
            return { valid: false, error: `Unknown column: ${columnKey}` };
          }
        }
      }

      // Check if all functions are supported
      const funcCalls = formula.match(/\b([A-Z_]+)\s*\(/g);
      if (funcCalls) {
        for (const call of funcCalls) {
          const funcName = call.replace(/\s*\($/, '');
          if (!this.functions[funcName]) {
            return { valid: false, error: `Unknown function: ${funcName}` };
          }
        }
      }

      return { valid: true, error: null };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  }

  /**
   * Get list of available functions with descriptions
   * @returns {array} Array of function info objects
   */
  getAvailableFunctions() {
    return [
      { name: 'SUM', description: 'Sum of values', example: 'SUM([nettWeight], 100)' },
      {
        name: 'AVG',
        description: 'Average of values',
        example: 'AVG([nettWeight], [grossWeight])',
      },
      { name: 'MIN', description: 'Minimum value', example: 'MIN([nettWeight], 0)' },
      { name: 'MAX', description: 'Maximum value', example: 'MAX([nettWeight], 100)' },
      { name: 'ROUND', description: 'Round to decimals', example: 'ROUND([nettWeight], 2)' },
      { name: 'ABS', description: 'Absolute value', example: 'ABS([nettWeight])' },
      {
        name: 'IF',
        description: 'Conditional',
        example: 'IF([ticketType] = "Dispatch", [nettWeight], 0)',
      },
      {
        name: 'IFS',
        description: 'Multiple conditions',
        example: 'IFS([nettWeight] < 50, "Light", [nettWeight] < 100, "Medium", "Heavy")',
      },
      {
        name: 'AND',
        description: 'Logical AND',
        example: 'AND([nettWeight] > 0, [ticketType] = "Dispatch")',
      },
      {
        name: 'OR',
        description: 'Logical OR',
        example: 'OR([nettWeight] > 100, [ticketType] = "Receipt")',
      },
      { name: 'NOT', description: 'Logical NOT', example: 'NOT([ticketType] = "Dispatch")' },
      { name: 'UPPER', description: 'Convert to uppercase', example: 'UPPER([product])' },
      { name: 'LOWER', description: 'Convert to lowercase', example: 'LOWER([product])' },
      { name: 'LEN', description: 'String length', example: 'LEN([vehicleReg])' },
      {
        name: 'CONCAT',
        description: 'Concatenate strings',
        example: 'CONCAT([ticketNo], "-", [deliveryNo])',
      },
      { name: 'LEFT', description: 'Left substring', example: 'LEFT([vehicleReg], 3)' },
      { name: 'RIGHT', description: 'Right substring', example: 'RIGHT([vehicleReg], 4)' },
      { name: 'TRIM', description: 'Remove whitespace', example: 'TRIM([product])' },
      {
        name: 'LOOKUP',
        description: 'Lookup value from another row',
        example: 'LOOKUP([deliveryNo], "ticketNo", "vehicleReg")',
      },
    ];
  }
}

const formulaEvaluator = new FormulaEvaluator();
export default formulaEvaluator;
